import { api } from "@/lib/api";

export const OFFICE_FORMATS = ["docx", "xlsx", "pdf"];

export function formatOfficeBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

export async function loadOfficeWorkspace(signal) {
  const [{ data: workspace }, { data: capabilities }] = await Promise.all([
    api.get("/me/workspace", { signal }),
    api.get("/office-tools/capabilities", { signal }),
  ]);
  const { data: files } = await api.get(
    `/workspaces/${workspace.workspace_id}/office-files`,
    { signal }
  );
  return {
    workspaceId: workspace.workspace_id,
    capabilities,
    files: Array.isArray(files) ? files : [],
  };
}

export async function uploadOfficeFile(workspaceId, title, file) {
  const body = new FormData();
  body.append("title", title);
  body.append("file", file);
  const { data } = await api.post(`/workspaces/${workspaceId}/office-files`, body);
  return data;
}

export async function getEditorConfig(workspaceId, fileId) {
  const platform = window.matchMedia?.("(max-width: 767px)")?.matches ? "mobile" : "desktop";
  const lang = (document.documentElement.lang || navigator.language || "en").toLowerCase().startsWith("ar")
    ? "ar"
    : "en";
  const { data } = await api.get(
    `/workspaces/${workspaceId}/office-files/${fileId}/editor-config`,
    { params: { platform, lang } }
  );
  return data;
}

export async function downloadOfficeFile(workspaceId, file) {
  const response = await api.get(
    `/workspaces/${workspaceId}/office-files/${file.file_id}/download`,
    { responseType: "blob" }
  );
  const href = URL.createObjectURL(response.data);
  try {
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = file.original_filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

export async function deleteOfficeFile(workspaceId, fileId) {
  await api.delete(`/workspaces/${workspaceId}/office-files/${fileId}`);
}

export async function createOfficeToolJob(workspaceId, command, options, files = []) {
  const body = new FormData();
  body.append("command", command);
  body.append("options", JSON.stringify(options || {}));
  files.forEach((file) => body.append("files", file));
  const { data } = await api.post(
    `/workspaces/${workspaceId}/office-tool-jobs`,
    body
  );
  return data;
}

export async function listOfficeToolJobs(workspaceId, signal) {
  const { data } = await api.get(
    `/workspaces/${workspaceId}/office-tool-jobs`,
    { signal }
  );
  return Array.isArray(data) ? data : [];
}

export async function cancelOfficeToolJob(workspaceId, jobId) {
  await api.delete(`/workspaces/${workspaceId}/office-tool-jobs/${jobId}`);
}

export async function downloadOfficeToolResult(workspaceId, jobId, result) {
  const response = await api.get(
    `/workspaces/${workspaceId}/office-tool-jobs/${jobId}/results/${result.index}`,
    { responseType: "blob" }
  );
  const href = URL.createObjectURL(response.data);
  try {
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = result.filename || "result";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}
